import React, { useContext, useState } from "react"
import moment from "moment"
import { Sparkles } from "lucide-react"

import { db } from "../../../utils/firebase"
import { ModalContext } from "../../../utils/providers/modal"
import Button from "../../../components/ui/button/button"

import ParseWindow from "./parse-window"
import { runParse } from "./run-parse"
import { FieldsCompletedCell, ParseStatusBadge } from "./row-cells"

/**
 * Single `<tr>` for the Enquiry Queue table. Mirrors the `MailboxRow`
 * pattern — denormalised display fields on the queue doc + a couple of
 * action buttons. The Parse button opens the modal that does the actual
 * runEmailAiTool call and writes the result back to the doc.
 */
function QueueRow({ entry }) {
	const { showModal } = useContext(ModalContext)
	const [showingParse, setShowingParse] = useState(false)
	const [parsing, setParsing] = useState(false)
	const [removing, setRemoving] = useState(false)

	const handleParse = async () => {
		setParsing(true)
		try {
			await runParse(entry)
			// Row data refreshes via the parent's onSnapshot listener — no
			// local state to update here.
		} catch (err) {
			showModal({
				type: "ALERT",
				title: "Parse failed",
				body: err?.message || "Could not run parse.",
				cancel: { label: "Dismiss", action: () => null },
				next: { label: "Dismiss", action: () => null },
			})
		} finally {
			setParsing(false)
		}
	}

	const forwardedAt = entry.forwarded_at?.seconds
		? moment.unix(entry.forwarded_at.seconds).format("D MMM YYYY, HH:mm")
		: entry.received_at?.seconds
		? moment.unix(entry.received_at.seconds).format("D MMM YYYY, HH:mm")
		: null

	const parsedAt = entry.last_parse_at?.seconds
		? moment.unix(entry.last_parse_at.seconds).format("D MMM YYYY, HH:mm")
		: null

	// Client identity columns are pulled out of the saved parse result. Field
	// keys match the registry defaults; if the user renames them on the
	// Development tab the column will just go blank for older parses, which
	// matches the "feedback loop" framing — the table reflects what the
	// current parser actually produced.
	const parsed = entry.last_parse_result || null
	const clientName =
		[parsed?.lead_first_name, parsed?.lead_last_name].filter(Boolean).join(" ") || null
	const clientEmail = parsed?.lead_email || null
	const clientPhone = parsed?.lead_phone || null

	const confirmRemove = () => {
		showModal({
			type: "ALERT",
			title: "Remove from queue?",
			body:
				"This deletes the queued enquiry from the development queue. The original communication record (if any) is left untouched.",
			cancel: { label: "Cancel", action: () => null },
			next: {
				label: "Yes, remove",
				action: async () => {
					setRemoving(true)
					try {
						await db.collection("enquiry_queue").doc(entry.id).delete()
					} catch (err) {
						setRemoving(false)
						showModal({
							type: "ALERT",
							title: "Failed to remove",
							body: err?.message || "Could not delete queue entry.",
							cancel: { label: "Dismiss", action: () => null },
							next: { label: "Dismiss", action: () => null },
						})
					}
				},
			},
		})
	}

	// Stop button clicks from bubbling up to the row's onClick — without this,
	// hitting Remove/Parse would also open the modal.
	const stop = (handler) => (event) => {
		event.stopPropagation()
		handler?.(event)
	}

	return (
		<>
			<tr
				data-system-id={entry.id}
				className="queue-row is-clickable"
				onClick={() => setShowingParse(true)}>
				<td>{forwardedAt || <small className="no-value">—</small>}</td>

				<td>{parsedAt || <small className="no-value">—</small>}</td>

				<td>{clientName || <small className="no-value">—</small>}</td>

				<td>{clientEmail || <small className="no-value">—</small>}</td>

				<td>{clientPhone || <small className="no-value">—</small>}</td>

				<td className="queue-row-status-cell">
					<ParseStatusBadge
						hasParsedAt={!!entry.last_parse_at}
						parsed={entry.last_parse_result}
					/>
				</td>

				<td>
					<FieldsCompletedCell
						parsed={entry.last_parse_result}
						fields={entry.last_parse_settings?.fields}
					/>
				</td>

				<td className="is-button">
					<Button
						small={true}
						label={entry.last_parse_at ? "Reparse" : "Parse"}
						icon={<Sparkles size={12} />}
						loading={parsing}
						loadingText="Parsing..."
						disabled={parsing}
						onClick={stop(handleParse)}
					/>

					<Button
						small={true}
						label="Remove"
						loading={removing}
						loadingText="Removing..."
						onClick={stop(confirmRemove)}
					/>
				</td>
			</tr>

			{showingParse && (
				<ParseWindow
					entry={entry}
					close={() => setShowingParse(false)}
				/>
			)}
		</>
	)
}

export default QueueRow
